import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const RefundPolicy: React.FC = () => {
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isDarkMode } = useTheme();

  return (
    <div className="app">
      <div className="container" style={{ maxWidth: '800px', padding: '20px' }}>
        {/* Header with Back Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '32px',
            paddingTop: '20px'
          }}
        >
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              padding: '8px',
              marginLeft: '-8px',
              transition: 'opacity 0.2s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <ArrowLeft size={20} />
            Back
          </button>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{
            color: 'var(--text-primary)',
            lineHeight: '1.7'
          }}
        >
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            marginBottom: '8px',
            color: 'var(--text-primary)'
          }}>
            Refund Policy
          </h1>
          
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '14px',
            marginBottom: '32px'
          }}>
            Last Updated: December 31, 2025
          </p>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              Overview
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              At miiaitor, we are committed to providing high-quality AI-generated tour experiences. 
              This Refund Policy explains the circumstances under which refunds may be issued for 
              credit purchases and tour generation services.
            </p>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              This policy should be read in conjunction with our{' '}
              <Link 
                to="/terms-of-service" 
                style={{ 
                  color: 'var(--primary-color)', 
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                Terms of Service
              </Link>
              , which govern your use of the miiaitor platform.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              1. General Refund Policy
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Due to the nature of our service—which involves real-time AI research, content generation, 
              and audio narration—we maintain a strict no-refund policy for successfully completed tour 
              generations. Once a tour has been created and delivered to you, the computational resources, 
              AI processing, and third-party API costs have been incurred and cannot be reversed.
            </p>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              All credit purchases are final and non-refundable, except in cases of technical failure 
              as described below.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              2. Refunds for Failed Tour Generations
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We will issue refunds or credit restoration in the following circumstances:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Complete Generation Failure:</strong> If our system fails to generate your tour 
                and deducts a credit, we will restore the credit to your account immediately upon verification.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>System Error During Payment:</strong> If you are charged but credits are not added 
                to your account due to a technical error, we will either add the credits or issue a full refund.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Duplicate Charges:</strong> If you are accidentally charged multiple times for the 
                same purchase, we will refund the duplicate charges.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              3. Non-Refundable Circumstances
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Refunds will NOT be issued in the following situations:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                <strong>Successfully Generated Tours:</strong> Once a tour has been successfully created 
                and delivered, no refund is available, even if you are unsatisfied with the content.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Unused Credits:</strong> Credits that remain in your account, whether purchased 
                individually or through a subscription, are non-refundable.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Change of Mind:</strong> If you decide you no longer want the service after 
                purchasing credits or completing a tour generation, no refund will be provided.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Content Dissatisfaction:</strong> As tours are generated by AI based on publicly 
                available information and your selected parameters, we cannot offer refunds based on 
                subjective dissatisfaction with tour content, style, or recommendations.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>Audio Generation Issues:</strong> If the tour text is successfully generated but 
                audio narration fails or is delayed, the tour is still considered successfully delivered. 
                You can retry audio generation or contact support for assistance, but no refund will be issued.
              </li>
              <li style={{ marginBottom: '8px' }}>
                <strong>User Error:</strong> Refunds are not available for tours generated with incorrect 
                location selections, interest choices, or place selections made by the user.
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              4. Subscription Cancellations
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Monthly subscriptions can be cancelled at any time through your Account settings. When you 
              cancel a subscription:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>
                You will retain access to your subscription credits until the end of your current billing period
              </li>
              <li style={{ marginBottom: '8px' }}>
                No refunds will be issued for the remaining days in the current billing cycle
              </li>
              <li style={{ marginBottom: '8px' }}>
                Any unused subscription credits from the current period will expire at the end of the billing cycle
              </li>
              <li style={{ marginBottom: '8px' }}>
                You will not be charged for subsequent billing periods
              </li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              To cancel your subscription, navigate to Account → Manage Subscription → Cancel Subscription.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              5. How to Request a Refund
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              If you believe you qualify for a refund under this policy, please contact us at:
            </p>
            <div style={{
              backgroundColor: 'var(--secondary-color)',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <p style={{ margin: '0', color: 'var(--text-primary)', fontWeight: '600' }}>
                Email: <a 
                  href="mailto:miaitorapp@gmail.com"
                  style={{
                    color: 'var(--primary-color)',
                    textDecoration: 'none'
                  }}
                >
                  miaitorapp@gmail.com
                </a>
              </p>
            </div>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Please include the following information in your refund request:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Your account email address</li>
              <li style={{ marginBottom: '8px' }}>Transaction ID or payment reference</li>
              <li style={{ marginBottom: '8px' }}>Date and time of the purchase or tour generation</li>
              <li style={{ marginBottom: '8px' }}>Detailed description of the issue</li>
              <li style={{ marginBottom: '8px' }}>Screenshots or error messages (if applicable)</li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We aim to respond to all refund requests within 2-3 business days. Approved refunds will be 
              processed within 5-10 business days to the original payment method.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              6. Credit Expiration
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              One-time credit purchases never expire. You can use them at any time without time restrictions.
            </p>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              Subscription credits refresh monthly at the start of each billing period. Unused subscription 
              credits do not roll over to the next month and will expire when the billing period ends or 
              when you cancel your subscription.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              7. Payment Disputes and Chargebacks
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              If you initiate a chargeback or payment dispute through your bank or credit card company 
              without first contacting us, we reserve the right to:
            </p>
            <ul style={{ paddingLeft: '24px', marginBottom: '16px', color: 'var(--text-secondary)' }}>
              <li style={{ marginBottom: '8px' }}>Immediately suspend or terminate your account</li>
              <li style={{ marginBottom: '8px' }}>Remove access to all purchased credits and generated tours</li>
              <li style={{ marginBottom: '8px' }}>Ban your account from future use of the service</li>
            </ul>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We strongly encourage you to contact us directly to resolve any payment issues before 
              initiating a dispute with your payment provider. We are committed to working with you 
              to find a fair resolution.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              8. Third-Party Payment Processors
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              All payments are processed through Paystack, our secure payment provider. Paystack's 
              terms and refund policies also apply to transactions. For questions about payment 
              processing, please refer to Paystack's documentation or contact our support team.
            </p>
          </section>

          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              9. Changes to This Policy
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              We reserve the right to modify this Refund Policy at any time. Any changes will be 
              posted on this page with an updated "Last Updated" date. Your continued use of the 
              Service after changes are posted constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section style={{ marginBottom: '48px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '16px' }}>
              10. Contact Us
            </h2>
            <p style={{ marginBottom: '16px', color: 'var(--text-secondary)' }}>
              If you have any questions about this Refund Policy or need assistance with a refund 
              request, please contact us:
            </p>
            <div style={{
              backgroundColor: 'var(--card-background)',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              padding: '20px',
              textAlign: 'center'
            }}>
              <a
                href="mailto:miaitorapp@gmail.com"
                className="btn btn-primary"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none'
                }}
              >
                Contact Support
              </a>
            </div>
          </section>
        </motion.div>
      </div>
    </div>
  );
};

export default RefundPolicy;

